/**
 * Утилита для кросс-доменной авторизации (tripsider.com -> lk.youtravel.me)
 */

/**
 * Проверяет, является ли текущий домен TripSider (EN версия)
 * Прод: tripsider.com
 * Стенд: ytcrm-XXXX.b-en.ytme.tech
 * 
 * @returns {boolean}
 */
export function isTripSiderDomain() {
  const hostname = fakeWindow.location.hostname;
  
  // Стенд EN: *.b-en.ytme.tech
  if (/\.b-en\.ytme\.tech$/.test(hostname)) {
    return true;
  }
  
  // Прод: tripsider.com
  if (hostname.includes('tripsider.com')) {
    return true;
  }
  
  return false;
}

/**
 * Получает URL основного сайта youtravel.me с учетом окружения
 * Для стенда: ytcrm-XXXX.b.ytme.tech
 * Для прода: из VUE_APP_BASE_URL
 * 
 * @returns {string}
 */
export function getMainSiteUrl() {
  const hostname = fakeWindow.location.hostname;
  
  // Стенд EN: ytcrm-XXXX.b-en.ytme.tech -> ytcrm-XXXX.b.ytme.tech
  const standMatch = hostname.match(/^(ytcrm-\d+)\.b-en\.ytme\.tech$/);
  if (standMatch) {
    return `https://${standMatch[1]}.b.ytme.tech`;
  }
  
  // Прод tripsider -> используем VUE_APP_BASE_URL или youtravel.me
  if (hostname.includes('tripsider.com')) {
    const baseUrl = process.env.VUE_APP_BASE_URL;
    // Если в env указан стенд или локальный URL, возвращаем youtravel.me
    if (baseUrl && baseUrl.includes('youtravel.me')) {
      return baseUrl.replace(/\/$/, '');
    }
    return 'https://youtravel.me';
  }
  
  // Уже на основном сайте — используем VUE_APP_BASE_URL или текущий хост
  const baseUrl = process.env.VUE_APP_BASE_URL;
  if (baseUrl) {
    return baseUrl.replace(/\/$/, '');
  }
  
  return `${fakeWindow.location.protocol}//${hostname}`;
}

/**
 * Получает URL личного кабинета (ЛК) с учетом окружения
 * Для стенда: lk.ytcrm-XXXX.b.ytme.tech
 * Для прода: из VUE_APP_NEWLK_FRONT_HOST
 * 
 * @returns {string}
 */
export function getLkUrl() {
  const hostname = fakeWindow.location.hostname;
  
  // Стенд: ytcrm-XXXX.b.ytme.tech или ytcrm-XXXX.b-en.ytme.tech -> lk.ytcrm-XXXX.b.ytme.tech
  const standMatch = hostname.match(/^(ytcrm-\d+)\.b(?:-en)?\.ytme\.tech$/);
  if (standMatch) {
    return `https://lk.${standMatch[1]}.b.ytme.tech`;
  }
  
  // Прод или другие окружения — используем VUE_APP_NEWLK_FRONT_HOST
  const lkHost = process.env.VUE_APP_NEWLK_FRONT_HOST;
  if (lkHost) {
    return lkHost.replace(/\/$/, '');
  }
  
  return 'https://lk.youtravel.me';
}

/**
 * Формирует URL для кросс-доменной авторизации через youtravel.me
 * 
 * @param {string} userId - ID пользователя
 * @param {string} token - Dateless token
 * @param {string} [redirectTo='lk'] - Куда редиректить после авторизации ('lk', 'dashboard', или URL)
 * @param {string} [redirectSource='tripsider'] - Источник редиректа (сохраняется в cookie на бэкенде)
 * @returns {string} URL для редиректа
 */
export function buildCrossDomainAuthUrl(userId, token, redirectTo = 'lk', redirectSource = 'tripsider') {
  const mainSiteUrl = getMainSiteUrl();
  
  // Формируем токен: base64(userId:token)
  const authToken = btoa(`${userId}:${token}`);
  
  // URL с параметрами авторизации
  // redirectTo - куда редиректить после авторизации
  // redirectSource - передаем на бэкенд, там сохраняется в cookie как одноразовый триггер
  const url = `${mainSiteUrl}/?t=${encodeURIComponent(authToken)}&redirectTo=${encodeURIComponent(redirectTo)}&redirectSource=${encodeURIComponent(redirectSource)}`;
  
  return url;
}

/**
 * Проверяет, нужен ли редирект на ЛК после авторизации
 * Условия:
 * 1. Текущий домен - TripSider
 * 2. Пользователь - эксперт (userRole === 'Expert')
 * 
 * @param {string} userRole - Роль пользователя
 * @returns {boolean}
 */
export function shouldRedirectToLk(userRole) {
  return isTripSiderDomain() && userRole === 'Expert';
}

/**
 * Выполняет кросс-доменный редирект на ЛК через youtravel.me
 * 
 * @param {Object} authData - Данные авторизации
 * @param {string|number} authData.userId - ID пользователя
 * @param {string} authData.token - Dateless token
 * @param {string} authData.userRole - Роль пользователя
 * @returns {boolean} - true если редирект выполнен, false если не нужен
 */
export function performCrossDomainRedirect(authData) {
  const { userId, token, userRole } = authData;
  
  if (!shouldRedirectToLk(userRole)) {
    return false;
  }
  
  if (!userId || !token) {
    console.warn('[CrossDomainAuth] Missing userId or token');
    return false;
  }
  
  const redirectUrl = buildCrossDomainAuthUrl(userId, token);
  
  // Выполняем редирект
  fakeWindow.location.href = redirectUrl;
  
  return true;
}
